import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  Router as RouterIcon,
  Wifi as WifiIcon,
  Phone as PhoneIcon,
  Tv as TvIcon,
} from '@mui/icons-material';
import { ContratoServicos, Contract } from '../../types';
import { ContratoServicosService } from '../../services/contratoServicosService';
import { contractService } from '../../services/contractService';
import { getStatusColor, getStatusLabel } from '../../utils/contractStatusUtils';

interface ContractServicosSelectProps {
  value?: number | string | null;
  onChange: (servicoId: number | null, servico?: ContratoServicos, contractId?: number) => void;
  clientId?: number | null; // Filtrar por cliente
  contractId?: number | null; // Filtrar por contrato específico
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  onlyActive?: boolean; // Mostrar apenas serviços ativos
  allowClear?: boolean;
  showContractTitle?: boolean; // Mostrar título do contrato nos itens
}

const ContractServicosSelect: React.FC<ContractServicosSelectProps> = ({
  value = null,
  onChange,
  clientId = null,
  contractId = null,
  label = 'Serviço',
  placeholder = 'Selecione um serviço',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  variant = 'outlined',
  onlyActive = true,
  allowClear = true,
  showContractTitle = true,
}) => {
  const [servicos, setServicos] = useState<ContratoServicos[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [servicosByContract, setServicosByContract] = useState<Record<number, ContratoServicos[]>>({});

  // Carregar contratos do cliente
  useEffect(() => {
    if (clientId && !contractId) {
      loadContracts();
    } else {
      setContracts([]);
    }
  }, [clientId, contractId]);

  // Carregar serviços
  useEffect(() => {
    if (contractId) {
      // Carregar serviços de um contrato específico
      loadServicos(contractId);
    } else if (clientId && contracts.length > 0) {
      // Carregar serviços de todos os contratos do cliente
      contracts.forEach(contract => {
        loadServicos(contract.id);
      });
    } else if (!clientId && !contractId) {
      // Limpar se não houver filtros
      setServicos([]);
      setServicosByContract({});
    }
  }, [contractId, clientId, contracts]);

  const loadContracts = async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setLoadError(null);

      const response = await contractService.getByClientId(clientId, {
        limit: 100,
      });

      if (response.success && response.data) {
        setContracts(response.data);
      } else {
        setLoadError('Erro ao carregar contratos');
        setContracts([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar contratos:', error);
      setLoadError('Erro ao carregar contratos do cliente');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadServicos = async (contractIdParam: number) => {
    try {
      setLoading(true);
      setLoadError(null);

      const response = await ContratoServicosService.getByContractId(contractIdParam);

      if (response.success && response.data) {
        const servicosData = response.data || [];
        
        // Filtrar apenas serviços ativos se necessário
        const filteredServicos = onlyActive
          ? servicosData.filter(s => s.status !== 'cancelado')
          : servicosData;

        // Atualizar estado agrupado por contrato
        setServicosByContract(prev => ({
          ...prev,
          [contractIdParam]: filteredServicos,
        }));

        // Atualizar lista plana de serviços
        if (contractId) {
          setServicos(filteredServicos);
        } else {
          setServicos(prev => {
            const existingIds = new Set(prev.map(s => s.id));
            const newServicos = filteredServicos.filter(s => !existingIds.has(s.id));
            return [...prev, ...newServicos];
          });
        }
      }
    } catch (error: any) {
      console.error(`Erro ao carregar serviços do contrato ${contractIdParam}:`, error);
      setLoadError('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    
    if (!selectedValue || selectedValue === '') {
      onChange(null, undefined, undefined);
      return;
    }

    const servicoId = Number(selectedValue);
    const servico = servicos.find(s => s.id === servicoId);

    if (servico) {
      // Encontrar o contrato do serviço
      let foundContractId: number | undefined;
      for (const [contractId, servicosList] of Object.entries(servicosByContract)) {
        if (servicosList.some(s => s.id === servicoId)) {
          foundContractId = Number(contractId);
          break;
        }
      }

      onChange(servicoId, servico, foundContractId);
    }
  };

  const getServicoIcon = (tipo: string) => {
    switch (tipo) {
      case 'internet':
        return <WifiIcon fontSize="small" />;
      case 'telefonia':
        return <PhoneIcon fontSize="small" />;
      case 'tv':
        return <TvIcon fontSize="small" />;
      default:
        return <RouterIcon fontSize="small" />;
    }
  };

  const formatServicoLabel = (servico: ContratoServicos, contract?: Contract) => {
    const parts: string[] = [];
    
    if (showContractTitle && contract) {
      parts.push(contract.titulo);
    }
    
    parts.push(servico.tipo);
    
    if (servico.plano?.nome) {
      parts.push(servico.plano.nome);
    }
    
    if (servico.usuario_pppoe) {
      parts.push(`(${servico.usuario_pppoe})`);
    }

    return parts.join(' - ');
  };

  // Preparar opções agrupadas por contrato
  const getGroupedOptions = () => {
    if (contractId) {
      // Se há um contrato específico, mostrar apenas os serviços desse contrato
      const servicosList = servicosByContract[contractId] || [];
      return servicosList.map(servico => ({
        servico,
        contract: contracts.find(c => c.id === contractId),
      }));
    }

    // Agrupar por contrato
    const grouped: Array<{ servico: ContratoServicos; contract?: Contract }> = [];
    
    for (const [contractIdStr, servicosList] of Object.entries(servicosByContract)) {
      const contractIdNum = Number(contractIdStr);
      const contract = contracts.find(c => c.id === contractIdNum);
      
      servicosList.forEach(servico => {
        grouped.push({ servico, contract });
      });
    }

    return grouped;
  };

  const groupedOptions = getGroupedOptions();
  const selectedServico = servicos.find(s => s.id === Number(value));

  return (
    <FormControl
      fullWidth={fullWidth}
      error={error}
      disabled={disabled || loading}
      size={size}
      variant={variant}
      required={required}
    >
      {loadError && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
          {loadError}
        </Alert>
      )}

      <InputLabel>{label + (required ? ' *' : '')}</InputLabel>
      <Select
        value={value !== null && value !== undefined && value !== '' ? value.toString() : ''}
        label={label}
        onChange={handleChange}
        disabled={disabled || loading || (!clientId && !contractId)}
        renderValue={(selected) => {
          if (!selected || selected === '') return '';
          const servico = servicos.find(s => s.id === Number(selected));
          if (!servico) return '';
          
          const contract = contracts.find(c => {
            const servicosList = servicosByContract[c.id] || [];
            return servicosList.some(s => s.id === servico.id);
          });
          
          return formatServicoLabel(servico, contract);
        }}
        endAdornment={
          loading ? (
            <CircularProgress size={16} sx={{ mr: 2 }} />
          ) : undefined
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        {!clientId && !contractId ? (
          <MenuItem disabled>
            Selecione um cliente ou contrato primeiro
          </MenuItem>
        ) : groupedOptions.length === 0 && !loading ? (
          <MenuItem disabled>
            Nenhum serviço encontrado
          </MenuItem>
        ) : (
          (() => {
            const items: React.ReactNode[] = [];
            let lastContractId: number | null = null;

            groupedOptions.forEach(({ servico, contract }) => {
              // Adicionar header do contrato se mudou
              if (showContractTitle && contract && contract.id !== lastContractId) {
                if (lastContractId !== null) {
                  items.push(<Divider key={`divider-${contract.id}`} />);
                }
                items.push(
                  <MenuItem
                    key={`contract-${contract.id}`}
                    value=""
                    disabled
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'action.hover',
                      opacity: 1,
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <RouterIcon fontSize="small" />
                      <Typography variant="body2" fontWeight="bold">
                        {contract.titulo}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getStatusLabel(contract.status)}
                      color={getStatusColor(contract.status) as any}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </MenuItem>
                );
                lastContractId = contract.id;
              }

              // Adicionar serviço
              items.push(
                <MenuItem
                  key={servico.id}
                  value={servico.id.toString()}
                  sx={{ pl: showContractTitle && contract ? 4 : 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ color: 'text.secondary' }}>
                      {getServicoIcon(servico.tipo)}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {servico.tipo} - {servico.plano?.nome || 'Sem plano'}
                        {servico.usuario_pppoe && ` (${servico.usuario_pppoe})`}
                      </Typography>
                      {servico.endereco_inst && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {servico.endereco_inst}, {servico.numero_inst} - {servico.cidade_inst}/{servico.estado_inst}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={getStatusLabel(servico.status)}
                      color={getStatusColor(servico.status) as any}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                </MenuItem>
              );
            });

            return items;
          })()
        )}
      </Select>

      {helperText && (
        <FormHelperText sx={{ fontSize: '0.75rem' }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ContractServicosSelect;

